import { useState, useEffect } from 'react'
import { useAuth0 } from '@auth0/auth0-react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useUsersApi } from '../services/usersApi'
import { UserIcon, XMarkIcon } from '@heroicons/react/24/outline'
import PhotoUpload from '../components/PhotoUpload'
import PushNotificationSettings from '../components/PushNotificationSettings'
import toast from 'react-hot-toast'
import { formatPhoneNumberForInput } from '../utils/phoneUtils'

export default function ProfilePage() {
  const { user, getAccessTokenSilently } = useAuth0()
  const queryClient = useQueryClient()
  const usersApi = useUsersApi()
  const API_BASE_URL = import.meta.env.VITE_API_URL || import.meta.env.VITE_API_BASE_URL || 'https://api.midwestrailbirds.com/v1'

  // Fetch current user data
  const { data: userData, isLoading: isLoadingUser, error: userError } = useQuery({
    queryKey: ['user', 'profile'],
    queryFn: () => {
      return usersApi.getCurrentUser().then(data => {
        return data
      }).catch(error => {
        throw error
      })
    },
    enabled: !!user // Only run query when Auth0 user is available
  })

  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    mobile: '',
    bio: '',
    avatar: '',
    joinedYear: '',
    attendanceYears: [] as string[]
  })

  const [yearInput, setYearInput] = useState('')
  const [errors, setErrors] = useState<{ [key: string]: string }>({})

  const handleAddYear = () => {
    if (yearInput && /^\d{4}$/.test(yearInput)) {
      setFormData(prev => ({
        ...prev,
        attendanceYears: [...new Set([...prev.attendanceYears, yearInput])].sort()
      }))
      setYearInput('')
    }
  }

  const handleRemoveYear = (year: string) => {
    setFormData(prev => ({
      ...prev,
      attendanceYears: prev.attendanceYears.filter(y => y !== year)
    }))
  }

  // Update form data when user data is loaded
  useEffect(() => {
    if (userData || user) {
      setFormData({
        firstName: userData?.firstName || '',
        lastName: userData?.lastName || '',
        email: userData?.email || user?.email || '',
        mobile: userData?.mobile || '',
        bio: userData?.bio || '',
        avatar: userData?.avatar || '',
        joinedYear: userData?.joinedYear || '',
        attendanceYears: userData?.attendanceYears || []
      })
    }
  }, [userData, user])

  const updateProfileMutation = useMutation({
    mutationFn: (data: typeof formData) => usersApi.updateProfile({
      firstName: data.firstName,
      lastName: data.lastName,
      email: data.email,
      mobile: data.mobile,
      bio: data.bio,
      avatar: data.avatar,
      joinedYear: data.joinedYear,
      attendanceYears: data.attendanceYears
    }),
    onSuccess: (updatedData) => {
      // Update the cache with the new data
      queryClient.setQueryData(['user', 'profile'], updatedData)
      queryClient.setQueryData(['user'], updatedData)
      
      // Update the form data with the response
      setFormData(prev => ({
        ...prev,
        firstName: updatedData.firstName,
        lastName: updatedData.lastName,
        email: updatedData.email || prev.email,
        mobile: updatedData.mobile,
        bio: updatedData.bio || '',
        avatar: updatedData.avatar || '',
        joinedYear: updatedData.joinedYear,
        attendanceYears: updatedData.attendanceYears || []
      }))
      
      toast.success('Profile updated successfully!')
    },
    onError: (error: any) => {
      toast.error(error?.message || 'Failed to update profile')
    }
  })

  const handlePhotoUploaded = (photoUrl: string) => {
    setFormData(prev => ({ ...prev, avatar: photoUrl }))
    setErrors(prev => ({ ...prev, avatar: '' }))
    toast.success('Profile photo uploaded successfully!')
  }

  const handlePhotoRemoved = () => {
    setFormData(prev => ({ ...prev, avatar: '' }))
    setErrors(prev => ({ ...prev, avatar: '' }))
  }

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formatted = formatPhoneNumberForInput(e.target.value)
    setFormData(prev => ({ ...prev, mobile: formatted }))
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    
    // Basic email validation
    const newErrors: { [key: string]: string } = {}
    
    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address'
    }
    
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors)
      return
    }
    
    // Clear any previous errors
    setErrors({})
    updateProfileMutation.mutate(formData)
  }

  useEffect(() => {
  }, [user])

  useEffect(() => {
  }, [userData])

  if (isLoadingUser) {
    return (
      <div className="min-h-screen bg-rb-dark py-12">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white dark:bg-gray-800 shadow rounded-lg">
            <div className="px-4 py-5 sm:p-6">
              <div className="animate-pulse">
                <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded w-1/4 mb-8"></div>
                <div className="space-y-6">
                  <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                    {[1, 2, 3, 4, 5].map((i) => (
                      <div key={i}>
                        <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-1/4 mb-2"></div>
                        <div className="h-10 bg-gray-200 dark:bg-gray-700 rounded w-full"></div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-rb-dark py-12">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-white dark:bg-gray-800 shadow-xl rounded-2xl overflow-hidden backdrop-blur-xl">
          <div className="px-8 py-8">
            <div className="flex items-center justify-between mb-8">
              <h2 className="text-3xl font-bold bg-gradient-to-r from-green-600 to-green-400 bg-clip-text text-transparent">
                Profile Settings
              </h2>
              <button
                type="submit"
                form="profile-form"
                disabled={updateProfileMutation.isLoading}
                className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-xl text-white bg-gradient-to-r from-green-600 to-green-400 hover:from-green-700 hover:to-green-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 shadow-lg hover:shadow-xl"
              >
                {updateProfileMutation.isLoading ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Saving...
                  </>
                ) : (
                  'Save Changes'
                )}
              </button>
            </div>

            <form id="profile-form" onSubmit={handleSubmit} className="space-y-8">
              {/* Avatar Section */}
              <div className="flex items-start space-x-6 p-6 bg-gray-50 dark:bg-gray-700 rounded-xl">
                <div className="flex-shrink-0">
                  <div className="w-24 h-24 bg-primary-100 dark:bg-primary-900 rounded-full flex items-center justify-center overflow-hidden">
                    {formData.avatar ? (
                      <img 
                        src={formData.avatar} 
                        alt="Profile avatar"
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="text-primary-600 dark:text-primary-400 text-2xl font-semibold">
                        {formData.firstName.charAt(0).toUpperCase()}{formData.lastName.charAt(0).toUpperCase()}
                      </div>
                    )}
                  </div>
                </div>
                
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">Profile Photo</h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                    Upload a profile picture to help other members recognize you
                  </p>
                  
                  <PhotoUpload
                    currentPhotoUrl={formData.avatar}
                    onPhotoUploaded={handlePhotoUploaded}
                    onPhotoRemoved={handlePhotoRemoved}
                    className="mt-2"
                  />
                  {errors.avatar && <p className="mt-1 text-sm text-red-600">{errors.avatar}</p>}
                </div>
              </div>

              <PushNotificationSettings />

              <div className="grid grid-cols-1 gap-8 sm:grid-cols-2">
                <div className="space-y-6">
                  <div>
                    <label htmlFor="firstName" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      First Name
                    </label>
                    <input
                      type="text"
                      id="firstName"
                      value={formData.firstName}
                      onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                      className="block w-full px-4 py-3 rounded-xl border-0 bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm ring-1 ring-inset ring-gray-300 dark:ring-gray-600 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-green-500 sm:text-sm transition-all duration-200"
                    />
                  </div>

                  <div>
                    <label htmlFor="lastName" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Last Name
                    </label>
                    <input
                      type="text"
                      id="lastName"
                      value={formData.lastName}
                      onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                      className="block w-full px-4 py-3 rounded-xl border-0 bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm ring-1 ring-inset ring-gray-300 dark:ring-gray-600 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-green-500 sm:text-sm transition-all duration-200"
                    />
                  </div>

                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Email Address
                    </label>
                    <input
                      type="email"
                      id="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      className="block w-full px-4 py-3 rounded-xl border-0 bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm ring-1 ring-inset ring-gray-300 dark:ring-gray-600 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-green-500 sm:text-sm transition-all duration-200"
                      placeholder="your.email@example.com"
                    />
                    {errors.email && <p className="mt-1 text-sm text-red-600">{errors.email}</p>}
                  </div>

                  <div>
                    <label htmlFor="mobile" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Mobile Number
                    </label>
                    <input
                      type="tel"
                      id="mobile"
                      value={formData.mobile}
                      onChange={handlePhoneChange}
                      className="block w-full px-4 py-3 rounded-xl border-0 bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm ring-1 ring-inset ring-gray-300 dark:ring-gray-600 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-green-500 sm:text-sm transition-all duration-200"
                      placeholder="555-123-4567"
                    />
                  </div>

                  <div>
                    <label htmlFor="bio" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Bio
                    </label>
                    <textarea
                      id="bio"
                      rows={4}
                      value={formData.bio}
                      onChange={(e) => setFormData({ ...formData, bio: e.target.value })}
                      className="block w-full px-4 py-3 rounded-xl border-0 bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm ring-1 ring-inset ring-gray-300 dark:ring-gray-600 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-green-500 sm:text-sm transition-all duration-200 resize-none"
                      placeholder="Tell us about yourself and your interest in horse racing..."
                    />
                  </div>

                  <div>
                    <label htmlFor="joinedYear" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Member Since
                    </label>
                    <input
                      type="text"
                      id="joinedYear"
                      value={formData.joinedYear}
                      onChange={(e) => setFormData({ ...formData, joinedYear: e.target.value })}
                      className="block w-full px-4 py-3 rounded-xl border-0 bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm ring-1 ring-inset ring-gray-300 dark:ring-gray-600 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-green-500 sm:text-sm transition-all duration-200"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-4">
                    Years Attended
                  </label>
                  <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-6 shadow-inner">
                    <div className="grid grid-cols-3 sm:grid-cols-4 gap-3">
                      {/* Generate years from 2007 to current year, updating automatically */}
                    {Array.from(
                      { length: new Date().getFullYear() - 2007 + 1 }, 
                      (_, i) => (2007 + i)
                    ).sort((a, b) => b - a) // Sort in descending order (newest first)
                    .map(year => (
                        <label
                          key={year}
                          className={`relative flex items-center justify-center p-3 rounded-xl cursor-pointer transition-all duration-200 ${
                            formData.attendanceYears.includes(year.toString())
                              ? 'bg-green-100 dark:bg-green-900 ring-2 ring-green-500'
                              : 'bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-600 ring-1 ring-gray-200 dark:ring-gray-600'
                          }`}
                        >
                          <input
                            type="checkbox"
                            checked={formData.attendanceYears.includes(year.toString())}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setFormData(prev => ({
                                  ...prev,
                                  attendanceYears: [...prev.attendanceYears, year.toString()].sort()
                                }))
                              } else {
                                setFormData(prev => ({
                                  ...prev,
                                  attendanceYears: prev.attendanceYears.filter(y => y !== year.toString())
                                }))
                              }
                            }}
                            className="sr-only"
                          />
                          <span className={`text-sm font-medium ${
                            formData.attendanceYears.includes(year.toString())
                              ? 'text-green-900 dark:text-green-100'
                              : 'text-gray-900 dark:text-gray-100'
                          }`}>
                            {year}
                          </span>
                        </label>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  )
}
